import { useMemo } from 'react';
import {
    useCrossAreaSearchBarInputValue,
    useShowAreaPanel,
    useShowCrossAreaMessagePanel,
    useShowSearchBar,
    useStaredAreas,
    useWindowWidth
} from 'shared-state/display-options/hooks';
import { useCurrentAreaId, useCurrentOrgId } from 'shared-state/location/hook';
import { useAreaGroupListWithCount, useAreaListWithCount, useIsFavoritesDropdownVisible } from 'shared-state/directory/hooks';
import { useNavigate } from 'react-router';
import { SetterOrUpdater } from 'recoil';
import IvicosStrings from 'kits/language/stringKit';
export interface IAreaSwitchState {
    currentAreaId?: string;
    areaOptions: {
        key: string;
        title: string;
    }[];
    areaGroupOptions: {
        key: string;
        title: string;
        groupLevel: string;
        parentAreaId: string;
    }[];
    setShowAreaPanel: SetterOrUpdater<boolean>;
    handleSearchPersonIconClick: (event: any) => void;
    handleAreaGroupClick: (e: any, areaGroup: any) => void;
    handleAreaClick: (e: any, item: any) => void;
}

export const useAreaSwitchState: (color?: string) => IAreaSwitchState = (color) => {
    const routeHistory = useNavigate();
    const [, setShowAreaPanel] = useShowAreaPanel();
    const [orgId] = useCurrentOrgId();
    const [currentAreaId] = useCurrentAreaId();
    const [staredAreaIds] = useStaredAreas(orgId || 'default');
    const areasWithCount = useAreaListWithCount();
    const areaGroupsWithCount = useAreaGroupListWithCount();
    const [showSearchBar, setShowSearchBar] = useShowSearchBar();
    const [, setShowCrossAreaMessagePanel] = useShowCrossAreaMessagePanel();
    const [, setCrossAreaSearchBarInputValue] = useCrossAreaSearchBarInputValue();
    const { smallDevice, mediumDevice } = useWindowWidth();
    const [, setIsFavoritesDropdownVisible] = useIsFavoritesDropdownVisible();

    const areaOptions = useMemo(
        () =>
            areasWithCount
                ?.filter((area: any) => staredAreaIds.includes(area.id))
                .map((area: any) => ({ key: area.id, title: area.name, usersAndVisitorsCount: area.count })),
        [areasWithCount, staredAreaIds]
    );
    const areaGroupOptions = useMemo(
        () =>
            areaGroupsWithCount
                ?.filter((areaGroup: any) => staredAreaIds.includes(areaGroup.id))
                .map((areaGroup: any) => ({
                    key: areaGroup.id,
                    title: areaGroup.name,
                    groupLevel: areaGroup.groupLevel,
                    parentAreaId: areaGroup.parentAreaId,
                    usersAndVisitorsCount: areaGroup.count
                })),
        [areaGroupsWithCount, staredAreaIds]
    );

    const onClickOption = (e: any, k: string | null) => {
        if (currentAreaId && currentAreaId != 'null' && !confirm('Are you sure you want to leave this area?')) return;
        k && routeHistory('/areas/' + k);
    };

    const handleSearchPersonIconClick = (event) => {
        // stops the event propagation using event.stopPropagation() to prevent the click event from reaching the document click event listener.
        event.stopPropagation();
        if (smallDevice || mediumDevice) {
            setShowCrossAreaMessagePanel(true);
            setShowSearchBar(false);
        } else {
            setShowSearchBar(!showSearchBar);
            setShowCrossAreaMessagePanel(false);
        }

        setCrossAreaSearchBarInputValue('');
    };

    const handleAreaGroupClick = (e: any, areaGroup: any) => {
        e.stopPropagation();
        const { key, title, parentAreaId, groupLevel } = areaGroup;

        if (groupLevel === 'L0') {
            localStorage.setItem('l0AGTitle', title || 'LOBBY');
            localStorage.setItem('l0AGId', key || '');
        }
        if (groupLevel === 'L1' && parentAreaId) {
            const parentArea = areaGroupsWithCount.find((ag) => ag.id === parentAreaId);
            localStorage.setItem('l0AGTitle', parentArea?.name || 'LOBBY');
            localStorage.setItem('l0AGId', parentArea?.id || '');
            localStorage.setItem('l1AGTitle', title || IvicosStrings.areaGroup);
            localStorage.setItem('l1AGId', key || '');
        }

        const route = `/group/${key}/${groupLevel}/subAreas` || '/dashboard';
        routeHistory(route);
        setIsFavoritesDropdownVisible(false);
    };

    const handleAreaClick = (e: any, item: any) => {
        e.stopPropagation();
        const area = areasWithCount.find((area) => area.id === item.key);
        if (area) {
            if (area.parentAreaId && area.level !== 'L0') {
                if (area.level === 'L2') {
                    const parentAg = areaGroupsWithCount.find((ag) => ag.id === area.parentAreaId);
                    localStorage.setItem('l1AGTitle', parentAg.name || IvicosStrings.areaGroup);
                    localStorage.setItem('l1AGId', parentAg.id || '');

                    const parentOfParentAg = areaGroupsWithCount.find((ag) => ag.id === parentAg.parentAreaId);
                    localStorage.setItem('l0AGTitle', parentOfParentAg.name || 'LOBBY');
                    localStorage.setItem('l0AGId', parentOfParentAg.id || '');
                } else {
                    const parentAg = areaGroupsWithCount.find((ag) => ag.id === area.parentAreaId);
                    localStorage.setItem('l0AGTitle', parentAg.name || 'LOBBY');
                    localStorage.setItem('l0AGId', parentAg.id || '');
                    localStorage.setItem('l1AGTitle', IvicosStrings.areaGroup);
                    localStorage.setItem('l1AGId', '');
                }
            } else {
                localStorage.setItem('l0AGTitle', 'LOBBY');
                localStorage.setItem('l0AGId', '');
                localStorage.setItem('l1AGTitle', IvicosStrings.areaGroup);
                localStorage.setItem('l1AGId', '');
            }

            localStorage.setItem('areaLevel', JSON.stringify({ areaId: area.id, areaLevel: area?.level, parentAreaId: area?.parentAreaId }));

            onClickOption(e, area.id);
            setIsFavoritesDropdownVisible(false);
        }
    };

    return {
        currentAreaId,
        areaOptions,
        areaGroupOptions,
        color,
        setShowAreaPanel,
        handleSearchPersonIconClick,
        handleAreaGroupClick,
        handleAreaClick
    };
};
